'use client';

import { useEffect, useState } from 'react';
import { Send, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { conversationClient } from '@/lib/gupshup-client';
import type { GupshupTemplate } from '@/types/whatsapp';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  phoneNumber: string;
  /**
   * Templates and the send both belong to the conversation's LINE — required for aggregate
   * sessions, which span many lines and cannot resolve one from the phone number alone.
   */
  conversationId?: string;
  onTemplateSent?: () => void;
};

function extractParams(data: string): string[] {
  const matches = data.match(/\{\{(\d+)\}\}/g);
  if (!matches) return [];
  // Deduplicate and sort by number
  const unique = [...new Set(matches)];
  unique.sort((a, b) => {
    const numA = parseInt(a.replace(/\D/g, ''));
    const numB = parseInt(b.replace(/\D/g, ''));
    return numA - numB;
  });
  return unique;
}

const CATEGORY_STYLES: Record<string, string> = {
  MARKETING: 'bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-300',
  UTILITY: 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300',
  AUTHENTICATION: 'bg-muted text-muted-foreground',
};

// Translation keys per Gupshup category — resolved with t() at render time.
const CATEGORY_LABEL_KEYS: Record<string, string> = {
  MARKETING: 'templateSelector.categories.marketing',
  UTILITY: 'templateSelector.categories.utility',
  AUTHENTICATION: 'templateSelector.categories.authentication',
};

export function TemplateSelectorDialog({
  open,
  onOpenChange,
  phoneNumber,
  conversationId,
  onTemplateSent,
}: Props) {
  const { t } = useTranslation('conversations');
  const [templates, setTemplates] = useState<GupshupTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Selected template + params
  const [selected, setSelected] = useState<GupshupTemplate | null>(null);
  const [paramPlaceholders, setParamPlaceholders] = useState<string[]>([]);
  const [paramValues, setParamValues] = useState<string[]>([]);
  const [mediaUrl, setMediaUrl] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open) {
      fetchTemplates();
      setSelected(null);
      setParamPlaceholders([]);
      setParamValues([]);
      setMediaUrl('');
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const fetchTemplates = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await conversationClient.templates.list({ conversationId });
      if (response.status === 'success' && response.templates) {
        setTemplates(response.templates.filter((t) => t.status === 'APPROVED'));
      } else {
        setTemplates([]);
        if (response.status === 'error') {
          setError(response.message || t('templateSelector.loadError'));
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('templateSelector.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (template: GupshupTemplate) => {
    const placeholders = extractParams(template.data || '');
    setSelected(template);
    setParamPlaceholders(placeholders);
    setParamValues(new Array(placeholders.length).fill(''));
    // Media templates send with a hosted image URL; the template's own sample
    // media (still in our bucket) is the sensible default.
    setMediaUrl(template.mediaUrl ?? '');
    setError(null);
  };

  const handleBack = () => {
    setSelected(null);
    setParamPlaceholders([]);
    setParamValues([]);
    setMediaUrl('');
    setError(null);
  };

  const handleSend = async () => {
    if (!selected) return;

    setSending(true);
    setError(null);
    try {
      await conversationClient.templates.send({
        phoneNumber,
        templateId: selected.id,
        params: paramValues.length > 0 ? paramValues : undefined,
        mediaUrl: mediaUrl.trim() || undefined,
        conversationId,
      });
      onOpenChange(false);
      onTemplateSent?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('templateSelector.sendError'));
    } finally {
      setSending(false);
    }
  };

  const isImageTemplate = selected?.templateType?.toUpperCase() === 'IMAGE';
  const allParamsFilled = paramPlaceholders.length === 0 || paramValues.every((v) => v.trim().length > 0);
  const mediaUrlValid = !isImageTemplate || mediaUrl.trim().length > 0;

  // Params form view
  if (selected) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{t('templateSelector.title')}</DialogTitle>
            <DialogDescription>{selected.elementName}</DialogDescription>
          </DialogHeader>

          <div className="bg-chat-panel rounded-md p-3 text-sm text-chat-text whitespace-pre-wrap">
            {selected.data}
          </div>

          {isImageTemplate && (
            <div className="space-y-1">
              <Label htmlFor="media-url" className="text-sm">
                {t('templateSelector.imageUrlLabel')} <span className="text-red-500 dark:text-red-400">*</span>
              </Label>
              <Input
                id="media-url"
                value={mediaUrl}
                onChange={(e) => setMediaUrl(e.target.value)}
                placeholder={t('templateSelector.imageUrlPlaceholder')}
                className="bg-background"
              />
              <p className="text-xs text-muted-foreground">
                {selected.mediaUrl
                  ? t('templateSelector.imageUrlPrefilled')
                  : t('templateSelector.imageUrlHint')}
              </p>
            </div>
          )}

          {paramPlaceholders.length > 0 && (
            <div className="space-y-3">
              {paramPlaceholders.map((placeholder, i) => (
                <div key={placeholder} className="space-y-1">
                  <Label htmlFor={`param-${i}`} className="text-sm">
                    {t('templateSelector.paramLabel', { name: placeholder })}
                  </Label>
                  <Input
                    id={`param-${i}`}
                    value={paramValues[i]}
                    onChange={(e) => {
                      const next = [...paramValues];
                      next[i] = e.target.value;
                      setParamValues(next);
                    }}
                    placeholder={t('templateSelector.paramPlaceholder', { name: placeholder })}
                    className="bg-background"
                  />
                </div>
              ))}
            </div>
          )}

          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800/60 rounded-lg text-sm text-red-800 dark:text-red-300">{error}</div>
          )}

          <Separator />

          <div className="flex justify-between gap-2">
            <Button variant="outline" onClick={handleBack}>
              {t('templateSelector.back')}
            </Button>
            <Button
              onClick={handleSend}
              disabled={!allParamsFilled || !mediaUrlValid || sending}
              className="bg-primary hover:bg-primary/90"
            >
              {sending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Send className="h-4 w-4 mr-1" />
                  {t('templateSelector.send')}
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Template list view
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t('templateSelector.title')}</DialogTitle>
          <DialogDescription>
            {t('templateSelector.selectDescription', { phoneNumber })}
          </DialogDescription>
        </DialogHeader>

        {error && (
          <div className="p-3 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800/60 rounded-lg text-sm text-red-800 dark:text-red-300">{error}</div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : templates.length === 0 ? (
          <div className="py-8 text-center text-muted-foreground">
            {t('templateSelector.noApproved')}
          </div>
        ) : (
          <ScrollArea className="h-[400px] pr-4">
            <div className="space-y-3">
              {templates.map((template) => (
                <div
                  key={template.id}
                  className="p-4 border border-border rounded-lg hover:bg-muted transition-colors cursor-pointer"
                  onClick={() => handleSelect(template)}
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-foreground truncate">{template.elementName}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge
                          variant="secondary"
                          className={CATEGORY_STYLES[template.category] || 'bg-muted text-muted-foreground'}
                        >
                          {CATEGORY_LABEL_KEYS[template.category]
                            ? t(CATEGORY_LABEL_KEYS[template.category])
                            : template.category}
                        </Badge>
                        <span className="text-xs text-muted-foreground">{template.languageCode}</span>
                      </div>
                    </div>
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelect(template);
                      }}
                      size="sm"
                      className="bg-primary hover:bg-primary/90"
                    >
                      <Send className="h-4 w-4 mr-1" />
                      {t('templateSelector.send')}
                    </Button>
                  </div>
                  {template.data && (
                    <p className="text-xs text-muted-foreground line-clamp-2 whitespace-pre-wrap">{template.data}</p>
                  )}
                </div>
              ))}
            </div>
          </ScrollArea>
        )}

        <Separator />

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('templateSelector.cancel')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
